import React, { useState } from "react";
import toast from "react-hot-toast";
import StructureAddonConfigModal from "./StructureAddonConfigModal";
import {
  STATUTORY_FLAG_KEYS,
  normalizeNpsConfig,
  normalizeVpfConfig,
  normalizeMealVoucherConfig,
} from "../../../../utils/salaryStructureAddons";
import { PB_FREQUENCIES } from "../../../../utils/salaryStructureComponentRules";
import { payrollOpsService } from "../../../../services/api";

/**
 * Profile Salary Structure — Statutory & Benefits Applicability
 * Side-by-side: statutory flags | benefit opt-ins (short labels).
 */
export default function EmployeeStructureOptionsPanel({
  structure,
  employee,
  readOnly = false,
  lockReason = null,
  minEffectiveMonth = null,
  statutoryFlags,
  onStatutoryChange,
  employeeNps,
  employeeVpf,
  employeeMeal,
  onEmployeeAddonChange,
  structureAllowsPb = false,
  pbEnabled = false,
  onPbEnabledChange,
  pbAmount = "",
  onPbAmountChange,
  pbPeriod = "annual",
  onPbPeriodChange,
  pbFrequency = "annual",
  onPbFrequencyChange,
  packageSplit = null,
  darkMode = false,
}) {
  const [addonModal, setAddonModal] = useState(null);

  const structureAllowsNps = !!structure?.nps?.enabled;
  const structureAllowsVpf = !!structure?.vpf?.enabled;
  const structureAllowsMeal = !!structure?.mealVoucher?.enabled;
  const pfOn = statutoryFlags?.pfApplicable !== false;
  const hasBenefits =
    structureAllowsPb || structureAllowsNps || structureAllowsVpf || structureAllowsMeal;

  const box = darkMode
    ? "rounded-lg border border-[hsl(var(--ds-border))] bg-[#1f1f1f] p-3"
    : "rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3";

  const openConfigure = (key) => {
    if (readOnly) {
      toast.error(lockReason || "Structure is locked — use Salary Revision / Update Statutory");
      return;
    }
    setAddonModal(key);
  };

  const checkLabelClass = "inline-flex items-center gap-2 text-sm cursor-pointer";

  return (
    <div className="space-y-3">
      <div className={box}>
        <h4 className="mb-2 text-sm font-semibold text-[hsl(var(--ds-fg))]">
          Statutory & Benefits Applicability
        </h4>

        <div
          className={
            hasBenefits
              ? "grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-0 sm:divide-x sm:divide-[hsl(var(--ds-border))]"
              : ""
          }
        >
          <div className={hasBenefits ? "sm:pr-3" : ""}>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {STATUTORY_FLAG_KEYS.map((f) => (
                <label key={f.key} className={checkLabelClass}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    disabled={readOnly || (f.key === "ceilingPf" && !pfOn)}
                    checked={
                      f.key === "ceilingPf" && !pfOn
                        ? true
                        : statutoryFlags?.[f.key] !== false &&
                          (statutoryFlags?.[f.key] === true ||
                            (statutoryFlags?.[f.key] == null && f.defaultOn))
                    }
                    onChange={(e) => {
                      if (readOnly) return;
                      const checked = e.target.checked;
                      const next = { ...(statutoryFlags || {}) };
                      next[f.key] = checked;
                      if (f.key === "pfApplicable" && !checked) {
                        next.ceilingPf = true;
                        if (employeeVpf?.enabled) {
                          onEmployeeAddonChange?.("vpf", { ...employeeVpf, enabled: false });
                          toast("VPF turned off because PF is not applicable", { icon: "ℹ️" });
                        }
                      }
                      onStatutoryChange?.(next);
                    }}
                  />
                  <span className="text-[hsl(var(--ds-fg))]">{f.label}</span>
                </label>
              ))}
            </div>
            {!readOnly ? (
              <button
                type="button"
                className="mt-3 text-xs font-medium text-[hsl(var(--ds-primary))] hover:underline"
                onClick={() => openConfigure("statutory")}
              >
                Set effective month for statutory save…
              </button>
            ) : null}
          </div>

          {hasBenefits ? (
            <div className="space-y-2 sm:pl-3">
              {structureAllowsPb && (
                <label className={checkLabelClass}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={pbEnabled}
                    disabled={readOnly}
                    onChange={(e) => onPbEnabledChange?.(e.target.checked)}
                  />
                  <span className="text-[hsl(var(--ds-fg))]">Performance Bonus (PB)</span>
                </label>
              )}

              {structureAllowsNps && (
                <div>
                  <label className={checkLabelClass}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      disabled={readOnly}
                      checked={!!employeeNps?.enabled}
                      onChange={(e) => {
                        if (readOnly) return;
                        const enabled = e.target.checked;
                        onEmployeeAddonChange?.("nps", { ...(employeeNps || {}), enabled });
                        if (enabled) openConfigure("nps");
                      }}
                    />
                    <span className="text-[hsl(var(--ds-fg))]">Employer NPS</span>
                  </label>
                  {employeeNps?.enabled && !readOnly ? (
                    <button
                      type="button"
                      className="mt-1 ml-6 block text-xs font-medium text-[hsl(var(--ds-primary))] hover:underline"
                      onClick={() => openConfigure("nps")}
                    >
                      Configure…
                    </button>
                  ) : null}
                </div>
              )}

              {structureAllowsVpf && (
                <div>
                  <label className={checkLabelClass}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      disabled={readOnly || !pfOn}
                      checked={!!employeeVpf?.enabled && pfOn}
                      onChange={(e) => {
                        if (readOnly) return;
                        if (!pfOn) {
                          toast.error("VPF requires PF Applicable");
                          return;
                        }
                        const enabled = e.target.checked;
                        onEmployeeAddonChange?.("vpf", { ...(employeeVpf || {}), enabled });
                        if (enabled) openConfigure("vpf");
                      }}
                    />
                    <span className="text-[hsl(var(--ds-fg))]">VPF</span>
                  </label>
                  {employeeVpf?.enabled && pfOn && !readOnly ? (
                    <button
                      type="button"
                      className="mt-1 ml-6 block text-xs font-medium text-[hsl(var(--ds-primary))] hover:underline"
                      onClick={() => openConfigure("vpf")}
                    >
                      Configure…
                    </button>
                  ) : null}
                </div>
              )}

              {structureAllowsMeal && (
                <div>
                  <label className={checkLabelClass}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      disabled={readOnly}
                      checked={!!employeeMeal?.enabled}
                      onChange={(e) => {
                        if (readOnly) return;
                        const enabled = e.target.checked;
                        onEmployeeAddonChange?.("meal", { ...(employeeMeal || {}), enabled });
                        if (enabled) openConfigure("mealVoucher");
                      }}
                    />
                    <span className="text-[hsl(var(--ds-fg))]">Meal Voucher</span>
                  </label>
                  {employeeMeal?.enabled && !readOnly ? (
                    <button
                      type="button"
                      className="mt-1 ml-6 block text-xs font-medium text-[hsl(var(--ds-primary))] hover:underline"
                      onClick={() => openConfigure("mealVoucher")}
                    >
                      Configure…
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {structureAllowsPb && pbEnabled ? (
          <div className="mt-3 grid grid-cols-1 gap-2 border-t border-[hsl(var(--ds-border))] pt-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-[11px] text-[hsl(var(--ds-muted-fg))]">
                PB amount
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={pbAmount}
                disabled={readOnly}
                onChange={(e) => onPbAmountChange?.(e.target.value)}
                className="w-full rounded-md border border-[hsl(var(--ds-border))] px-2 py-1.5 text-sm"
                placeholder="e.g. 300000"
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-[hsl(var(--ds-muted-fg))]">
                PB period
              </label>
              <select
                value={pbPeriod}
                disabled={readOnly}
                onChange={(e) => onPbPeriodChange?.(e.target.value)}
                className="w-full rounded-md border border-[hsl(var(--ds-border))] px-2 py-1.5 text-sm"
              >
                <option value="annual">Annual</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Payout frequency
              </label>
              <select
                value={pbFrequency}
                disabled={readOnly}
                onChange={(e) => onPbFrequencyChange?.(e.target.value)}
                className="w-full rounded-md border border-[hsl(var(--ds-border))] px-2 py-1.5 text-sm"
              >
                {PB_FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            {Number(pbAmount) > 0 && !packageSplit?.error && (
              <p className="sm:col-span-3 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Fixed for breakdown: ₹
                {Number(packageSplit?.fixedAnnual || 0).toLocaleString("en-IN")} / year
              </p>
            )}
            {packageSplit?.error ? (
              <p className="sm:col-span-3 text-[11px] text-red-700">{packageSplit.error}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      <StructureAddonConfigModal
        isOpen={addonModal === "nps"}
        onClose={() => setAddonModal(null)}
        title="Configure Employer NPS"
        addonKey="nps"
        level="employee"
        initialConfig={employeeNps || {}}
        structureDefaults={normalizeNpsConfig(structure?.nps)}
        requireEffectiveMonth
        minEffectiveMonth={minEffectiveMonth}
        readOnly={readOnly}
        onSave={(payload) =>
          onEmployeeAddonChange?.("nps", { ...payload, enabled: true })
        }
      />
      <StructureAddonConfigModal
        isOpen={addonModal === "vpf"}
        onClose={() => setAddonModal(null)}
        title="Configure VPF"
        addonKey="vpf"
        level="employee"
        initialConfig={employeeVpf || {}}
        structureDefaults={normalizeVpfConfig(structure?.vpf)}
        pfApplicable={pfOn}
        requireEffectiveMonth
        minEffectiveMonth={minEffectiveMonth}
        readOnly={readOnly}
        onSave={(payload) =>
          onEmployeeAddonChange?.("vpf", { ...payload, enabled: true })
        }
      />
      <StructureAddonConfigModal
        isOpen={addonModal === "mealVoucher"}
        onClose={() => setAddonModal(null)}
        title="Configure Meal Voucher"
        addonKey="mealVoucher"
        level="employee"
        initialConfig={employeeMeal || {}}
        structureDefaults={normalizeMealVoucherConfig(structure?.mealVoucher)}
        requireEffectiveMonth
        minEffectiveMonth={minEffectiveMonth}
        readOnly={readOnly}
        onSave={(payload) =>
          onEmployeeAddonChange?.("meal", { ...payload, enabled: true })
        }
      />
      <StructureAddonConfigModal
        isOpen={addonModal === "statutory"}
        onClose={() => setAddonModal(null)}
        title="Statutory effective month"
        addonKey="statutory"
        level="employee"
        initialConfig={{}}
        requireEffectiveMonth
        minEffectiveMonth={minEffectiveMonth}
        readOnly={readOnly}
        onSave={async (payload) => {
          if (!employee?.id) return;
          try {
            await payrollOpsService.createStatutoryApplicability(employee.id, {
              effectiveFrom: payload.effectiveFrom,
              pfApplicable: statutoryFlags?.pfApplicable !== false,
              ceilingPf: statutoryFlags?.ceilingPf !== false,
              epsApplicable: !!statutoryFlags?.epsApplicable,
              esiApplicable: !!statutoryFlags?.esiApplicable,
              ptApplicable: statutoryFlags?.ptApplicable !== false,
              gratuityApplicable: statutoryFlags?.gratuityApplicable !== false,
              remarks: "Updated from Profile Salary Structure",
            });
            toast.success("Statutory applicability saved");
            onStatutoryChange?.(statutoryFlags, { effectiveFrom: payload.effectiveFrom });
          } catch (err) {
            toast.error(err?.response?.data?.message || err.message || "Failed to save statutory");
            throw err;
          }
        }}
      />
    </div>
  );
}
